// modals/ObjectiveSuggestions.js - COMPOSANT SUGGESTIONS avec affichage simplifié et expansion
import {
    Award,
    ChevronDown,
    ChevronUp,
    Clock,
    Lightbulb,
    Plus,
    TrendingUp
} from 'lucide-react';
import { useState } from 'react';

const ObjectiveSuggestions = ({
    formData,
    categorySuggestions,
    loadingSuggestions,
    errorSuggestions,
    selectedCategory,
    isSubmitting,
    onApplySuggestion
}) => {
    const [expandedCards, setExpandedCards] = useState(new Set());

    const toggleExpansion = (suggestionId, event) => {
        event.stopPropagation();
        const newExpanded = new Set(expandedCards);
        if (newExpanded.has(suggestionId)) {
            newExpanded.delete(suggestionId);
        } else {
            newExpanded.add(suggestionId);
        }
        setExpandedCards(newExpanded);
    };

    const getDifficultyColor = (difficulty) => {
        switch (difficulty) {
            case 'easy': return 'bg-green-100 text-green-700';
            case 'medium': return 'bg-yellow-100 text-yellow-700';
            case 'hard': return 'bg-orange-100 text-orange-700';
            case 'expert': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const getDifficultyLabel = (difficulty) => {
        switch (difficulty) {
            case 'easy': return 'Facile';
            case 'medium': return 'Moyen';
            case 'hard': return 'Difficile';
            case 'expert': return 'Expert';
            default: return 'Non défini';
        }
    };

    const renderEmptyState = () => {
        if (!formData.category || formData.category === 'autres') {
            return (
                <div className="text-center py-8 text-gray-500">
                    <Lightbulb className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">
                        Sélectionnez une catégorie pour voir les suggestions d'objectifs
                    </p>
                </div>
            );
        }

        if (categorySuggestions.length === 0 && !loadingSuggestions) {
            return (
                <div className="text-center py-8 text-gray-500">
                    <Lightbulb className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">
                        Aucune suggestion disponible pour cette catégorie
                    </p>
                </div>
            );
        }

        return null;
    };

    const renderSuggestionCard = (suggestion, index) => {
        const suggestionId = suggestion.id || index;
        const isExpanded = expandedCards.has(suggestionId);

        return (
            <div
                key={suggestionId}
                className={`p-4 bg-[#fff3e6] rounded-lg border-2 border-dashed border-orange-300 transition-colors group ${!isExpanded ? 'hover:bg-[#ffad65] hover:border-orange-400' : ''
                    }`}
            >
                {/* En-tête accordéon - toujours visible */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1 min-w-0">
                        <Lightbulb className="w-4 h-4 text-orange-600 mr-3 flex-shrink-0" />
                        <h5 className="font-medium text-gray-900 text-sm line-clamp-1">
                            {suggestion.name}
                        </h5>
                    </div>

                    <div className="flex items-center space-x-3">
                        {suggestion.difficulty && (
                            <span className="text-xs px-2 py-1 bg-orange-100 text-orange-600 rounded-full">
                                {getDifficultyLabel(suggestion.difficulty)}
                            </span>
                        )}

                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleExpansion(suggestionId, e);
                            }}
                            className="p-1 hover:bg-orange-200 rounded-full transition-colors"
                        >
                            {isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-orange-500" />
                            ) : (
                                <ChevronDown className="w-4 h-4 text-orange-500" />
                            )}
                        </button>

                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                !isSubmitting && onApplySuggestion(suggestion);
                            }}
                            className="p-1 hover:bg-orange-200 rounded-full transition-colors"
                            disabled={isSubmitting}
                        >
                            <Plus className="w-4 h-4 text-orange-500 group-hover:text-orange-700 transition-colors" />
                        </button>
                    </div>
                </div>

                {/* Contenu développable */}
                {isExpanded && (
                    <div className="mt-4 space-y-3 border-t border-orange-200 pt-3">
                        {/* Description */}
                        {suggestion.description && (
                            <p className="text-sm text-gray-700">
                                {suggestion.description}
                            </p>
                        )}

                        {/* Statistiques en une ligne */}
                        <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-1">
                                    <TrendingUp className="w-4 h-4 text-orange-500" />
                                    <span className="text-gray-800 font-medium">
                                        {suggestion.target?.toLocaleString ? suggestion.target.toLocaleString() : suggestion.target}
                                        {suggestion.unit || selectedCategory?.defaultUnit || ''}
                                    </span>
                                </div>
                                <div className="flex items-center space-x-1">
                                    <Clock className="w-4 h-4 text-orange-500" />
                                    <span className="text-gray-800 font-medium">
                                        {suggestion.timeline} {suggestion.timelineUnit}
                                    </span>
                                </div>
                                <div className="flex items-center space-x-1">
                                    <Award className="w-4 h-4 text-orange-500" />
                                    <span className="text-gray-700">
                                        Confiance: {suggestion.confidence}%
                                    </span>
                                </div>
                            </div>

                            <div className="flex items-center space-x-2">
                                {suggestion.estimatedYield && (
                                    <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded-full">
                                        Rendement: {suggestion.estimatedYield}
                                    </span>
                                )}
                                {suggestion.estimatedInvestment && (
                                    <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded-full">
                                        Invest: {(suggestion.estimatedInvestment / 1000)}k€
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Tags */}
                        {suggestion.tags && suggestion.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {suggestion.tags.slice(0, 5).map((tag, tagIndex) => (
                                    <span key={tagIndex} className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded">
                                        {tag}
                                    </span>
                                ))}
                                {suggestion.tags.length > 5 && (
                                    <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded">
                                        +{suggestion.tags.length - 5}
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Conseils */}
                        {suggestion.tips && suggestion.tips.length > 0 && (
                            <div className="p-3 rounded-lg">
                                <h6 className="text-sm font-medium text-gray-800 mb-2">
                                    Conseils ({suggestion.tips.length})
                                </h6>
                                <div className="space-y-2">
                                    {suggestion.tips.map((tip, tipIndex) => (
                                        <p key={tipIndex} className="text-sm text-gray-700 pl-3 border-l-2 border-orange-300">
                                            {tip}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div>
            {errorSuggestions && (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <p className="text-orange-600 text-sm">
                        Erreur lors du chargement des suggestions.
                    </p>
                </div>
            )}

            {/* Affichage conditionnel selon la catégorie sélectionnée */}
            {renderEmptyState() || (
                <div className="w-full">
                    <div className="max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-orange-300 scrollbar-track-orange-100">
                        <div className="space-y-4 pr-3">
                            {categorySuggestions
                                .sort((a, b) => {
                                    const difficultyOrder = { 'easy': 1, 'medium': 2, 'hard': 3, 'expert': 4 };
                                    const aOrder = difficultyOrder[a.difficulty] || 999;
                                    const bOrder = difficultyOrder[b.difficulty] || 999;
                                    return aOrder - bOrder;
                                })
                                .map((suggestion, index) =>
                                    renderSuggestionCard(suggestion, index)
                                )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ObjectiveSuggestions;